import { useEffect, useState } from "react";
import styles from "./createGame.module.css";
import clsx from "clsx";

import QuestionSetStep from "./steps/02_questionSetStep";
import NameAndTypeStep from "./steps/01_nameAndTypeStep";
import StatementsStep from "./steps/03_statementsStep";
import PartiesStep from "./steps/04_partiesStep";
import SettingsStep from "./steps/05_settingsStep";
import { translate } from "../../../translations/index.ts";
import ConfirmationStep from "./steps/06_confirmationStep";
import APIclient from "../../../api-client";

export function GameTemplateForm({ template, submit }) {
  const [activeStep, setActiveStep] = useState(1);
  const [newGameTemplate, setNewGameTemplate] = useState({ ...template });
  const [questionSets, selectQuestionSets] = useState([]);
  const [parties, setParties] = useState([]);
  const [statements, setStatements] = useState([]);

  useEffect(() => {
    fetchQuestionSets();
    if (template.questionSet) {
      fetchQuestionSetDependants(template.questionSet);
    }
  }, []);

  const fetchQuestionSets = async () => {
    const sets = await APIclient.questionSets.index();
    selectQuestionSets(sets.filter((set) => !set.hidden));
  };

  // Fetch the details and set some defaults before actually selecting the question set
  const setSelectedQuestionSet = async (id) => {
    fetchQuestionSetDependants(id, true);
  };

  const fetchQuestionSetDependants = async (id, forceNewStatements) => {
    const details = await APIclient.questionSets.read(id);
    setStatements(details.statements);
    setParties(
      details.parties.sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
      ),
    );

    if (!newGameTemplate._id || forceNewStatements) {
      const newTemplate = { ...newGameTemplate, questionSet: id };
      newTemplate.statements = details.statements.map((s) => s._id);
      newTemplate.parties = details.parties.map((p) => p._id);
      setNewGameTemplate(newTemplate);
    }
  };

  const goToNextStep = () => {
    if (!currentActionOk()) return;

    setActiveStep(activeStep + 1);
  };

  const goToPrevStep = () => {
    setActiveStep(activeStep - 1);
  };

  const currentActionOk = () => {
    return (
      (activeStep === 1 && newGameTemplate.name.length > 0) ||
      (activeStep === 2 && newGameTemplate.questionSet) ||
      (activeStep === 3 && newGameTemplate.statements.length > 0) ||
      (activeStep === 4 && newGameTemplate.parties.length > 0) ||
      activeStep > 4
    );
  };

  return (
    <div className={clsx(styles.createGameWrapper, "container")}>
      <h1 className={styles.title}>
        {translate([
          "teacher",
          "gameSettings",
          newGameTemplate._id ? "edit" : "create",
          "title",
        ])}
      </h1>
      <div className={styles.stepsContainer}>
        {activeStep === 1 && (
          <NameAndTypeStep
            template={newGameTemplate}
            setTemplate={setNewGameTemplate}
          />
        )}
        {activeStep === 2 && (
          <QuestionSetStep
            template={newGameTemplate}
            setQuestionSet={setSelectedQuestionSet}
            questionSets={questionSets}
          />
        )}
        {activeStep === 3 && (
          <StatementsStep
            template={newGameTemplate}
            setTemplate={setNewGameTemplate}
            statements={statements}
          />
        )}
        {activeStep === 4 && (
          <PartiesStep
            template={newGameTemplate}
            setTemplate={setNewGameTemplate}
            parties={parties}
          />
        )}
        {activeStep === 5 && (
          <SettingsStep
            template={newGameTemplate}
            setTemplate={setNewGameTemplate}
          />
        )}
        {activeStep === 6 && (
          <ConfirmationStep
            template={newGameTemplate}
            submit={submit}
            parties={parties}
          />
        )}
      </div>
      <section className={styles.controlsContainer}>
        <div className={styles.controlWrapper}>
          {activeStep < 6 && (
            <button
              className="button"
              onClick={goToNextStep}
              aria-disabled={!currentActionOk()}
            >
              {translate("teacher.gameSettings.nextStep")}
            </button>
          )}
        </div>
        <div className={styles.controlWrapper}>
          {activeStep > 1 && (
            <button className={styles.backBtn} onClick={goToPrevStep}>
              {translate("teacher.gameSettings.previousStep")}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
